import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'

interface MarkdownProps {
  children: string
  className?: string
  density?: 'default' | 'chat'
}

export function Markdown({
  children,
  className,
  density = 'default',
}: MarkdownProps) {
  return (
    <div
      className={cn(
        // Base prose styling with better spacing and word wrapping
        'prose max-w-none dark:prose-invert break-words wrap-anywhere',
        
        // Headings with better spacing and typography
        'prose-headings:font-semibold prose-headings:text-foreground prose-headings:tracking-tight',
        'prose-h1:text-2xl prose-h1:mt-8 prose-h1:mb-6 prose-h1:pb-2 prose-h1:border-b prose-h1:border-border',
        'prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-4',
        'prose-h3:text-lg prose-h3:font-bold prose-h3:mt-5 prose-h3:mb-3',
        'prose-h4:text-base prose-h4:font-bold prose-h4:mt-4 prose-h4:mb-2',
        'prose-h5:text-sm prose-h5:mt-3 prose-h5:mb-2',
        'prose-h6:text-sm prose-h6:mt-3 prose-h6:mb-2 prose-h6:font-medium',
        
        // Paragraphs with optimal spacing
        'prose-p:text-foreground prose-p:leading-7 prose-p:my-4',
        
        // Lists - using custom components for better control
        
        // Code blocks with enhanced styling and word wrapping
        'prose-pre:bg-muted/50 prose-pre:border prose-pre:border-border prose-pre:rounded-lg',
        'prose-pre:p-4 prose-pre:my-6 prose-pre:max-w-full prose-pre:whitespace-pre-wrap prose-pre:break-words',
        'prose-pre:shadow-sm prose-pre:text-[0.875rem] prose-pre:leading-6',
        
        // Inline code with better padding and word wrapping
        'prose-code:bg-muted/80 prose-code:text-foreground prose-code:px-2 prose-code:py-1',
        'prose-code:rounded prose-code:text-[0.875rem] prose-code:font-mono',
        'prose-code:before:content-none prose-code:after:content-none',
        'prose-code:border prose-code:border-border/50 prose-code:break-all prose-code:whitespace-normal',
        
        // Blockquotes with enhanced styling
        'prose-blockquote:border-l-4 prose-blockquote:border-primary/30',
        'prose-blockquote:bg-muted/20 prose-blockquote:px-4 prose-blockquote:py-3',
        'prose-blockquote:my-6 prose-blockquote:rounded-r-lg',
        'prose-blockquote:text-muted-foreground prose-blockquote:italic',
        'prose-blockquote:font-medium',
        
        // Tables are styled via explicit component overrides below
        
        // Links with enhanced visibility and hover effects
        'prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:font-medium prose-a:no-underline',
        'prose-a:transition-all prose-a:duration-200',
        'prose-a:decoration-blue-600/50 dark:prose-a:decoration-blue-400/50 prose-a:decoration-2',
        'hover:prose-a:text-blue-700 dark:hover:prose-a:text-blue-300 hover:prose-a:underline hover:prose-a:decoration-blue-600 dark:hover:prose-a:decoration-blue-400',
        
        // Horizontal rules with better spacing
        'prose-hr:border-border prose-hr:my-8 prose-hr:py-4 prose-hr:border-t-2',
        
        // Strong and emphasis
        'prose-strong:text-foreground prose-strong:font-semibold',
        'prose-em:text-foreground prose-em:italic',
        
        // Images with better spacing
        'prose-img:rounded-lg prose-img:border prose-img:border-border',
        'prose-img:my-6 prose-img:shadow-sm',

        density === 'chat' &&
          'leading-[1.7] [&_p]:leading-[1.7] [&_li]:leading-[1.7] [&_ol>li]:leading-[1.7] [&_ul>li]:leading-[1.7]',
        
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          // Enhanced headings with proper IDs for linking
          h1: ({ children, ...props }) => (
            <h1
              className={cn(
                "scroll-mt-16 border-b border-border pb-2 text-2xl font-semibold tracking-tight text-foreground",
                density === "chat" ? "mb-4 mt-6" : "mb-6 mt-8"
              )}
              {...props}
            >
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2
              className={cn(
                "scroll-mt-16 text-xl font-semibold tracking-tight text-foreground",
                density === "chat" ? "mb-3 mt-5" : "mb-4 mt-6"
              )}
              {...props}
            >
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3
              className={cn(
                "scroll-mt-16 text-lg font-semibold tracking-tight text-foreground",
                density === "chat" ? "mb-2 mt-4" : "mb-3 mt-5"
              )}
              {...props}
            >
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 className="mt-4 mb-2 scroll-mt-16 text-base font-semibold text-foreground" {...props}>
              {children}
            </h4>
          ),
          h5: ({ children, ...props }) => (
            <h5 className="mt-3 mb-2 scroll-mt-16 text-sm font-semibold text-foreground" {...props}>
              {children}
            </h5>
          ),
          h6: ({ children, ...props }) => (
            <h6 className="mt-3 mb-2 scroll-mt-16 text-sm font-medium text-muted-foreground" {...props}>
              {children}
            </h6>
          ),
          
          // Enhanced code blocks with language support and word wrapping
          code: (props) => {
            // react-markdown v8+ passes 'inline' and other props via CodeComponentProps
            // Import type from react-markdown for better type safety
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { inline, className, children, ...rest } = props as any;
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';

            if (!inline && match) {
              return (
                <div className="relative group max-w-full">
                  {language && (
                    <div className="absolute top-0 right-0 px-3 py-1 text-xs font-mono text-muted-foreground bg-muted border-l border-b border-border rounded-bl-md rounded-tr-lg z-10">
                      {language}
                    </div>
                  )}
                  <pre className="relative max-w-full whitespace-pre-wrap break-words">
                    <code className={cn('block max-w-full', className)} {...rest}>
                      {children}
                    </code>
                  </pre>
                </div>
              );
            }

            return (
              <code className="break-all whitespace-normal" {...rest}>
                {children}
              </code>
            );
          },
          
          // Enhanced links with external indicators, word wrapping, and clear hover state
          a: ({ href, children, ...props }) => {
            const isExternal = href?.startsWith('http');
            return (
              <a
                href={href}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className={cn(
                  "inline-flex items-center gap-1 break-all cursor-pointer",
                  "text-blue-600 dark:text-blue-400 font-medium no-underline",
                  "transition-all duration-200",
                  "hover:text-blue-700 dark:hover:text-blue-300 hover:underline hover:decoration-blue-600 dark:hover:decoration-blue-400 hover:decoration-2"
                )}
                {...props}
              >
                {children}
                {isExternal && (
                  <svg
                    className="w-3 h-3 opacity-70 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                )}
              </a>
            );
          },
          
          // Enhanced blockquotes
          blockquote: ({ children, ...props }) => (
            <blockquote className="relative" {...props}>
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary/30 rounded-full"></div>
              {children}
            </blockquote>
          ),
          
          // Enhanced tables with scroll container and proper cell layout
          table: ({ children, ...props }) => (
            <div className="my-6 max-w-full overflow-x-auto rounded-lg border border-border bg-card">
              <table className="min-w-full w-max border-collapse text-sm" {...props}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead className="bg-muted/40" {...props}>
              {children}
            </thead>
          ),
          tbody: ({ children, ...props }) => (
            <tbody className="[&>tr:last-child]:border-b-0" {...props}>
              {children}
            </tbody>
          ),
          tr: ({ children, ...props }) => (
            <tr className="border-b border-border" {...props}>
              {children}
            </tr>
          ),
          th: ({ children, ...props }) => (
            <th
              className="border-r border-border last:border-r-0 px-4 py-3 text-left align-top font-semibold text-foreground"
              {...props}
            >
              <div className="min-w-[7rem] whitespace-nowrap">{children}</div>
            </th>
          ),
          td: ({ children, ...props }) => (
            <td
              className="border-r border-border last:border-r-0 px-4 py-3 align-top text-foreground leading-6"
              {...props}
            >
              <div className="min-w-[7rem] max-w-[min(44ch,60vw)] whitespace-normal break-words">
                {children}
              </div>
            </td>
          ),
          
          // Enhanced unordered lists with proper nesting
          ul: ({ children, ...props }) => (
            <ul 
              className="my-3 space-y-1 list-disc text-foreground [&:not(li_&)]:ml-0 [&:not(li_&)]:pl-6 [&_li_&]:my-1 [&_li_&]:ml-0 [&_li_&]:pl-4"
              {...props}
            >
              {children}
            </ul>
          ),
          
          // Enhanced ordered lists with proper nesting
          ol: ({ children, ...props }) => (
            <ol 
              className="my-3 space-y-1 list-decimal text-foreground [&:not(li_&)]:ml-0 [&:not(li_&)]:pl-6 [&_li_&]:my-1 [&_li_&]:ml-0 [&_li_&]:pl-4"
              {...props}
            >
              {children}
            </ol>
          ),
          
          // Enhanced list items with proper spacing
          li: ({ children, ...props }) => (
            <li className="text-foreground leading-7" {...props}>
              {children}
            </li>
          ),
          
          // Task lists with custom checkboxes
          input: ({ checked, ...props }) => (
            <input
              type="checkbox"
              checked={checked}
              disabled
              className="mr-2 text-primary focus:ring-primary/20 rounded border-border"
              {...props}
            />
          ),

          // Safety net: detect paragraphs containing raw JSON, hash checksums, or diff markers
          p: ({ children, ...props }) => {
            const text = typeof children === "string" ? children : "";
            const looksLikeChecksum = /[a-f0-9]{32,}/i.test(text);
            const looksLikeDiff = /^\d+[acd]\d+$/m.test(text);

            // Detect raw JSON objects or arrays in paragraph text
            const trimmed = text.trim();
            const looksLikeJson =
              trimmed.length > 2 &&
              ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
               (trimmed.startsWith('[') && trimmed.endsWith(']')));

            let formattedJson: string | null = null;
            if (looksLikeJson) {
              try {
                const parsed = JSON.parse(trimmed);
                formattedJson = JSON.stringify(parsed, null, 2);
              } catch {
                // Not valid JSON — fall through to normal rendering
              }
            }

            if (formattedJson) {
              return (
                <div className="relative group max-w-full my-6">
                  <div className="absolute top-0 right-0 px-3 py-1 text-xs font-mono text-muted-foreground bg-muted border-l border-b border-border rounded-bl-md rounded-tr-lg z-10">
                    json
                  </div>
                  <pre className="bg-muted/50 border border-border rounded-lg p-4 max-w-full whitespace-pre-wrap break-words shadow-sm text-sm leading-6">
                    <code className="block max-w-full language-json">{formattedJson}</code>
                  </pre>
                </div>
              );
            }

            if (looksLikeChecksum || looksLikeDiff) {
              return (
                <pre className="bg-muted/50 border border-border rounded-lg p-4 my-6 max-w-full whitespace-pre-wrap break-words shadow-sm text-sm leading-6">
                  <code>{children}</code>
                </pre>
              );
            }
            return (
              <p
                className={cn(
                  "text-foreground",
                  density === "chat" ? "my-3 leading-[1.7]" : "my-4 leading-7"
                )}
                {...props}
              >
                {children}
              </p>
            );
          },

          hr: () => (
            <hr className="border-border my-3" />
          )
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
